type AppConfig = {
  BASE_URL: string;
  ENV: string;
  FEATURES: string[];
  SSO?: SSOConfig;
  MAP?: MapConfig;
};

/**
 * Raster tile sources for every Leaflet map in the app. Per deployment rather than
 * hardcoded: the public OSM servers' usage policy excludes heavy/commercial use, and an
 * air-gapped site can't reach them at all — those point this at a self-hosted server.
 *
 * `TILE_SOURCES` is ordered: the first entry is the default, used by every map that
 * doesn't pick one. `TILE_URL` + `TILE_ATTRIBUTION` is the single-source shorthand.
 */
export type MapConfig = {
  TILE_SOURCES?: TileSource[];
  TILE_URL?: string;
  TILE_ATTRIBUTION?: string;
};

export type TileSource = {
  id: string;
  /** Shown to dashboard authors as the map style's name. */
  name: string;
  url: string;
  attribution: string;
  /** The highest zoom the server has tiles for. The map zooms further, upscaling them. */
  maxZoom?: number;
  subdomains?: string | string[];
  /** `false` for sources that are dark already, or imagery, which inverts badly. */
  darkFilter?: boolean;
};

type SSOConfig = {
  appIdUri: string;
  tenantId: string;
  frontendClientId: string;
  redirectUri: string;
};

let config: AppConfig;

export async function loadConfig() {
  if (typeof window === "undefined") {
    console.info("Skipping config load during SSR");
    return;
  }

  if (config) return; // If already loaded, skip fetching again

  const res = await fetch("/config.json");
  config = await res.json();
  console.info("Loading config from /config.json", config);
}

export function updateBaseUrl(newBaseUrl: string) {
  if (!config) {
    throw new Error("Config not loaded. Call loadConfig() first.");
  }

  config.BASE_URL = newBaseUrl;
  console.info("Updated BASE_URL to", newBaseUrl);
}

export function getConfig(): AppConfig {
  if (!config) {
    throw new Error("Config not loaded. Call loadConfig() first.");
  }

  return config;
}

export function isConfigLoaded(): boolean {
  return !!config;
}

const DEFAULT_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const DEFAULT_TILE_ATTRIBUTION =
  '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors';

const OSM_TILE_SOURCE: TileSource = {
  id: "default",
  name: "OpenStreetMap",
  url: DEFAULT_TILE_URL,
  attribution: DEFAULT_TILE_ATTRIBUTION,
};

let tileSources: TileSource[] | undefined;

/**
 * The configured tile sources, never empty: `TILE_SOURCES`, else `TILE_URL`, else public
 * OSM. Bad entries are skipped with a warning rather than breaking every map.
 */
export function getMapTileSources(): TileSource[] {
  // Before the config loads there is nothing to cache; OSM stands in.
  if (!config) return [OSM_TILE_SOURCE];
  tileSources ??= parseTileSources(config.MAP);
  return tileSources;
}

/** `getMapTileSources` without the config singleton or the cache. Exported for tests. */
export function parseTileSources(map: MapConfig | undefined): TileSource[] {
  // config.json is hand-edited and never validated on load, so check the shape here.
  const raw: unknown = map?.TILE_SOURCES;
  if (raw != null && !Array.isArray(raw)) {
    console.warn("config.json: MAP.TILE_SOURCES must be an array", raw);
  }
  const valid: TileSource[] = [];
  for (const source of Array.isArray(raw) ? (raw as TileSource[]) : []) {
    if (
      !source ||
      typeof source.id !== "string" ||
      !source.id ||
      typeof source.url !== "string" ||
      !source.url
    ) {
      console.warn(
        "config.json: MAP.TILE_SOURCES entry needs an id and a url",
        source,
      );
    } else if (valid.some((s) => s.id === source.id)) {
      console.warn(`config.json: duplicate MAP.TILE_SOURCES id "${source.id}"`);
    } else {
      const { maxZoom, ...rest } = source;
      const validZoom = typeof maxZoom === "number" && Number.isFinite(maxZoom);
      if (maxZoom != null && !validZoom) {
        console.warn(
          `config.json: tile source "${source.id}" maxZoom must be a number; ignoring it`,
          maxZoom,
        );
      }
      valid.push({
        ...rest,
        ...(validZoom ? { maxZoom } : {}),
        name: source.name || source.id,
      });
    }
  }

  if (valid.length === 0 && map?.TILE_URL) {
    // A custom source's attribution is its own; the OSM credit would be wrong for it.
    valid.push({
      id: "default",
      name: "Default",
      url: map.TILE_URL,
      attribution: map.TILE_ATTRIBUTION ?? "",
    });
  }

  for (const source of valid) {
    if (!source.attribution) {
      console.warn(
        `config.json: tile source "${source.id}" has no attribution; most providers require one`,
      );
    }
  }

  return valid.length > 0 ? valid : [OSM_TILE_SOURCE];
}

/** The source with this id; an unknown or missing id is the default (first) source. */
export function resolveTileSource(id?: string): TileSource {
  const sources = getMapTileSources();
  return sources.find((s) => s.id === id) ?? sources[0];
}

/** Whether a stored source id still renders as itself. Unset means the default, so yes. */
export function isTileSourceAvailable(id?: string): boolean {
  return !id || getMapTileSources().some((s) => s.id === id);
}
